import range from "lodash/range";
import { jsx } from "panel";
import { SvgIconEl } from "common/components/svg-icon";
import { TooltipEl } from "common/components/tooltip";
import { Placement } from "common/components/tooltip/types";
import { PanelEl } from "common/components/panel";
import { Direction } from "common/components/panel/types";
import { handleKeyPress } from "common/util/focusable";
import { SidenavItemEl } from "./sidenav-item";
import { NestingOrder } from "./types";
import { ScrollPosition, SIDENAV_DEFAULT_WIDTH, SIDENAV_MAX_WIDTH, SIDENAV_MIN_WIDTH } from "./constants";
function Loader() {
  return /* @__PURE__ */ jsx("div", {
    sel: ".loader"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".mp-skeleton-header"
  }), range(9).map((_) => /* @__PURE__ */ jsx("div", {
    sel: ".mp-skeleton-large"
  })));
}
function SidenavItemWithNestedItems({
  section,
  item,
  idx,
  sectionIdx,
  selectedItemValue,
  hoveredItemValues,
  sectionDragDisabled,
  activeDragSectionIdx,
  onItemDrop,
  onItemDragStart,
  onItemDragEnd,
  onSelectItem,
  toggleItemChildren,
  onItemOverflowMenuChange,
  onItemOverflowMenuSelect,
  onItemMouseEntered,
  confirmNavigateFunc,
  isNestedDashboardsEnabled,
  expandedItemSet,
  deepLevel = 0,
  parentItemId = ``,
  nestedOrder = NestingOrder.First
}) {
  var _a, _b;
  const itemId = parentItemId ? `${parentItemId}-${item.value}` : `${sectionIdx}-${item.value}`;
  const nestedItemsToShow = expandedItemSet.has(itemId) && ((_a = item.nestedItems) == null ? void 0 : _a.length) ? item.nestedItems : [];
  return /* @__PURE__ */ jsx("div", {
    sel: ".sidenav-item"
  }, /* @__PURE__ */ jsx(SidenavItemEl, {
    attrs: {
      "is-selected": selectedItemValue && item.value === selectedItemValue,
      "is-new": item.isNew && !(hoveredItemValues == null ? void 0 : hoveredItemValues.has(item.value)),
      "is-draggable": section.dragDropEnabled,
      "is-disabled": sectionDragDisabled,
      "can-drop-item": Number.isInteger(activeDragSectionIdx),
      "label-tab-size": deepLevel,
      "is-nested-dashboards-enabled": isNestedDashboardsEnabled,
      "can-expand": ((_b = item.nestedItems) == null ? void 0 : _b.length) > 0,
      "is-expanded": expandedItemSet.has(itemId),
      "nested-order": nestedOrder
    },
    on: {
      itemDrop: onItemDrop,
      itemDragStart: onItemDragStart,
      itemDragEnd: onItemDragEnd,
      itemClick: onSelectItem,
      itemExpand: toggleItemChildren,
      overflowMenuChange: onItemOverflowMenuChange,
      overflowMenuSelect: onItemOverflowMenuSelect,
      mouseenter: () => onItemMouseEntered(item)
    },
    props: { confirmNavigateFunc, item: { ...item, itemIdx: idx, sectionIdx, itemId } }
  }), /* @__PURE__ */ jsx("div", {
    sel: ".sidenav-nested-items",
    class: { "is-selected": selectedItemValue && selectedItemValue === item.value }
  }, nestedItemsToShow.map((subItem, index) => /* @__PURE__ */ jsx(SidenavItemWithNestedItems, {
    section,
    item: subItem,
    sectionIdx,
    idx,
    selectedItemValue,
    hoveredItemValues,
    sectionDragDisabled,
    activeDragSectionIdx,
    onItemDrop,
    onItemDragStart,
    onItemDragEnd,
    onSelectItem,
    toggleItemChildren,
    onItemOverflowMenuChange,
    onItemOverflowMenuSelect,
    onItemMouseEntered,
    confirmNavigateFunc,
    deepLevel: deepLevel + 1,
    isNestedDashboardsEnabled,
    expandedItemSet,
    parentItemId: itemId,
    nestedOrder: index === item.nestedItems.length - 1 && (nestedOrder === NestingOrder.Last || nestedOrder === NestingOrder.First) ? NestingOrder.Last : NestingOrder.Middle
  }))));
}
function SidenavCtaEl({
  cta,
  isExpanded,
  isSelected,
  onClick
}) {
  const iconRenderer = (isCollapsed) => {
    return /* @__PURE__ */ jsx("div", {
      sel: ".icon-wrapper",
      class: { "is-selected": isSelected, collapsed: isCollapsed }
    }, /* @__PURE__ */ jsx(SvgIconEl, {
      attrs: { icon: cta.icon }
    }));
  };
  if (isExpanded) {
    return /* @__PURE__ */ jsx(SidenavItemEl, {
      sel: ".cta-item",
      props: { iconRenderer, item: cta },
      attrs: { "is-selected": isSelected },
      on: { itemClick: (ev) => onClick(ev.detail.value) }
    });
  } else {
    return /* @__PURE__ */ jsx("a", {
      sel: ".cta-item.collapsed",
      on: { click: () => onClick(cta.value), keypress: handleKeyPress },
      attrs: { value: cta.value, tabindex: 0, ...cta.href ? { href: cta.href } : {} }
    }, iconRenderer(true), /* @__PURE__ */ jsx(TooltipEl, {
      attrs: { placement: Placement.Right }
    }, cta.label));
  }
}
function SidenavSection({
  section,
  sectionDragDisabled,
  isExpanded,
  onToggleSection
}, sectionItemsChildren) {
  const isSectionCollapsible = section.label && section.isCollapsible;
  return /* @__PURE__ */ jsx("div", {
    sel: ".section",
    class: { "active-dragging-disabled": sectionDragDisabled }
  }, /* @__PURE__ */ jsx("div", {
    sel: ".section-header",
    attrs: { tabindex: 0 },
    on: isSectionCollapsible && { click: () => onToggleSection(section.label), keypress: handleKeyPress },
    class: { "is-collapsible": isSectionCollapsible }
  }, /* @__PURE__ */ jsx("div", {
    sel: ".section-header-label"
  }, section.label)), isExpanded && /* @__PURE__ */ jsx("div", {
    sel: ".section-items-list"
  }, sectionItemsChildren));
}
export function template() {
  var _a;
  const sections = this.state.sections;
  const selectedItemValue = this.helpers.getSelectedValue();
  const sidenavContent = this.attr(`loading`) ? /* @__PURE__ */ jsx(Loader, null) : sections.map((section, sectionIdx) => {
    const sectionDragDisabled = Number.isInteger(this.state.activeDragSectionIdx) && this.state.activeDragSectionIdx !== sectionIdx;
    const isExpanded = !this.state.collapsedSectionSet.has(section.label);
    if (section.items.length === 0) {
      return null;
    }
    return /* @__PURE__ */ jsx(SidenavSection, {
      section,
      sectionDragDisabled,
      isExpanded,
      onToggleSection: this.helpers.toggleSection
    }, isExpanded && section.items.map((item, idx) => /* @__PURE__ */ jsx("div", {
      sel: ".sidenav-item .sidenav-nested-items",
      class: {
        "is-selected": selectedItemValue && selectedItemValue === item.value,
        "is-child-selected": selectedItemValue && this.isItemOrChildSelected(item, selectedItemValue)
      },
      attrs: { value: item.value },
      hook: {
        insert: this.helpers.handleSidenavItemInserted
      }
    }, /* @__PURE__ */ jsx(SidenavItemWithNestedItems, {
      section,
      item,
      idx,
      sectionIdx,
      selectedItemValue,
      hoveredItemValues: this.state.hoveredItemValues,
      sectionDragDisabled,
      activeDragSectionIdx: this.state.activeDragSectionIdx,
      onItemDrop: this.helpers.onItemDrop,
      onItemDragStart: this.helpers.onItemDragStart,
      onItemDragEnd: this.helpers.onItemDragEnd,
      onSelectItem: this.helpers.onSelectItem,
      toggleItemChildren: this.helpers.toggleItemChildren,
      onItemOverflowMenuChange: this.helpers.onItemOverflowMenuChange,
      onItemOverflowMenuSelect: this.helpers.onItemOverflowMenuSelect,
      onItemMouseEntered: this.helpers.onItemMouseEntered,
      confirmNavigateFunc: this.confirmNavigateFunc,
      isNestedDashboardsEnabled: this.attr(`is-nested-dashboards-enabled`),
      expandedItemSet: this.state.expandedItemSet
    }))));
  });
  const header = this.attr(`header`);
  const footer = this.attr(`footer`);
  return /* @__PURE__ */ jsx(PanelEl, {
    sel: ".sidebar",
    attrs: {
      "storage-key": this.attr(`storage-key`),
      "min-adjacent-size": this.attr(`min-adjacent-width`),
      "max-size": SIDENAV_MAX_WIDTH,
      "min-size": SIDENAV_MIN_WIDTH,
      "panel-name": `Side Nav`,
      "resize-direction": Direction.Right,
      "default-ratio": 0.15,
      "default-size": SIDENAV_DEFAULT_WIDTH,
      "fixed-size": this.attr(`fixed-size`),
      "show-collapse-button": !this.attr(`fixed-size`),
      ...(footer == null ? void 0 : footer.actions) ? { "extra-actions": footer == null ? void 0 : footer.actions } : {},
      "style-override": `.wrapper .content-footer { border-color: var(--gray2); }`
    },
    on: {
      panelResize: (ev) => {
        const width = ev.detail.size;
        const isCollapsed = width < SIDENAV_MIN_WIDTH;
        this.update({ isCollapsed });
        this.dispatchEvent(new CustomEvent(`sidenavResize`, { detail: { width } }));
      },
      actionclick: (ev) => this.helpers.onFooterActionClick(ev.detail.value)
    },
    props: { containerEl: this.parentElement }
  }, /* @__PURE__ */ jsx("div", {
    sel: ".wrapper",
    class: {
      collapsed: this.state.isCollapsed
    },
    attrs: { elref: `wrapper` }
  }, header ? /* @__PURE__ */ jsx("div", {
    sel: ".header",
    attrs: { elref: `sidenavHeader` },
    class: {
      "is-expanded-style": this.helpers.isExpandedStyle(),
      "bottom-border": ![ScrollPosition.Top, ScrollPosition.None].includes(this.state.scrollPosition) && this.helpers.isExpandedStyle(),
      "slot-header": `slot` in header,
      "actions-container": `actions` in header
    }
  }, !this.state.isCollapsed ? /* @__PURE__ */ jsx("div", {
    sel: ".title"
  }, header.title) : null, `slot` in header ? /* @__PURE__ */ jsx("slot", {
    attrs: {
      name: this.state.isCollapsed && header.collapsedSlot ? header.collapsedSlot : header.slot
    }
  }) : (_a = header.actions) == null ? void 0 : _a.map((cta) => /* @__PURE__ */ jsx(SidenavCtaEl, {
    cta,
    isExpanded: this.helpers.isExpandedStyle(),
    isSelected: this.attr(`selected`) === cta.value,
    onClick: this.helpers.onHeaderActionClick
  }))) : null, !this.state.isCollapsed ? /* @__PURE__ */ jsx("div", {
    sel: ".sections-container",
    hook: { insert: this.helpers.onSectionsInserted },
    attrs: { elref: `sectionsContainer` }
  }, /* @__PURE__ */ jsx("div", {
    sel: ".sections"
  }, sidenavContent)) : null));
}
